/**
 * sc-cart-icon-badge
 *
 * Keeps the cart-icon count badge (rendered server-side via item_count in
 * snippets/sc-cart-icon.liquid) in sync with the live cart after AJAX
 * add/remove operations.
 *
 * Why this file is the way it is:
 *
 * 1. PUB_SUB_EVENTS is declared with `const` in assets/constants.js. In a
 *    browser global script, top-level `const` creates a LEXICAL global —
 *    accessible via bare identifier from any other script's global scope —
 *    but it is NOT a property of `window`. The previous version of this
 *    file guarded subscription on `typeof window.PUB_SUB_EVENTS !==
 *    'undefined'`, which is ALWAYS false, so subscribe never ran and the
 *    badge silently never updated. We use bare `PUB_SUB_EVENTS` here.
 *
 * 2. Even with our subscribe wired correctly, some publishers fire
 *    `window.publish(window.PUB_SUB_EVENTS.cartUpdate, ...)` — see
 *    sc-cart-drawer-mobile.js's optimistic-remove path — which throws
 *    silently because window.PUB_SUB_EVENTS is undefined. To catch those
 *    cases we wrap `window.fetch` and refresh on any response from
 *    /cart/add|change|update|clear. One small GET to /cart.js per cart
 *    mutation is the price of correctness.
 *
 * 3. cartUpdate publishers pass payloads with different shapes (line
 *    item vs full cart). We sidestep that by re-fetching /cart.js on
 *    every trigger and reading the canonical `item_count`.
 *
 * 4. Idempotent — re-imports / multiple snippet renders won't double-wire.
 */
(function () {
  if (window.__scCartIconBadgeWired) return;
  window.__scCartIconBadgeWired = true;

  const LOG = '[sc-cart-icon-badge]';

  function updateBadge(itemCount) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(itemCount);
      if (itemCount > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  // Debounce — pubsub + fetch-wrap can both fire for the same mutation;
  // collapse into one round-trip.
  let refreshTimer = null;
  function refreshFromCartApi(reason) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      const url =
        ((window.routes && window.routes.cart_url) || '/cart') + '.js';
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (cart && typeof cart.item_count === 'number') {
            console.log(LOG, 'refresh →', cart.item_count, '(' + reason + ')');
            updateBadge(cart.item_count);
          }
        })
        .catch(() => {
          /* silent — badge will catch up on next trigger */
        });
    }, 80);
  }

  // -- 1. pubsub subscription (bare identifier; see header comment) --
  function tryWirePubSub() {
    // typeof on a bare identifier doesn't throw ReferenceError if the
    // identifier is undeclared. Resolves via lexical scope, which
    // reaches the `const PUB_SUB_EVENTS` in constants.js.
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      return false;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      const src = (event && event.source) || 'unknown';
      console.log(LOG, 'cartUpdate received from', src);
      refreshFromCartApi('cartUpdate:' + src);
    });
    console.log(LOG, 'subscribed to PUB_SUB_EVENTS.cartUpdate');
    return true;
  }

  function deferredWire() {
    if (tryWirePubSub()) return;
    // pubsub.js / constants.js may load after this script — retry until
    // they show up.
    setTimeout(deferredWire, 100);
  }

  // -- 2. fetch wrapper fallback --
  //
  // Catches every cart-mutating AJAX call regardless of who made it.
  // Necessary because sc-cart-drawer-mobile.js's publish path is
  // currently a no-op (see header comment #2) so its optimistic remove
  // never fires cartUpdate — but it does POST /cart/change.js, which we
  // intercept here.
  if (typeof window.fetch === 'function') {
    const origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const promise = origFetch(input, init);
      const url =
        typeof input === 'string'
          ? input
          : (input && input.url) || '';
      if (/\/cart\/(add|change|update|clear)(\.js|\?|$)/i.test(url)) {
        promise
          .then(() => refreshFromCartApi('fetch:' + url.split('?')[0].split('/').pop()))
          .catch(() => {});
      }
      return promise;
    };
  }

  // -- 3. Refresh when the tab becomes visible again --
  // Covers cases where the cart changed in another tab / browser back.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      refreshFromCartApi('visibilitychange');
    }
  });
  // bfcache restore — broader resync than the badge.
  // When the browser restores a cached page (e.g. user hits back from
  // /checkouts after a Buy Now), the DOM is replayed in its pre-navigation
  // state. The local cart-icon badge re-fetches above, but other surfaces
  // (cart drawer item rows, sticky cart-count indicators, anything else
  // subscribing to cartUpdate) only update on a cartUpdate publish. Fetch
  // the canonical cart once and publish so every subscriber resyncs.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    const url = ((window.routes && window.routes.cart_url) || '/cart') + '.js';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || typeof cart.item_count !== 'number') return;
        console.log(LOG, 'bfcache resync →', cart.item_count);
        updateBadge(cart.item_count);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bfcache-resync',
            cartData: cart,
          });
        }
      })
      .catch(function (err) {
        console.warn(LOG, 'bfcache resync failed', err);
      });
  });

  deferredWire();
})();
